import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowLeft, Zap, Clock, Shield, Eye, Unplug, Search, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [referralCode, setReferralCode] = useState('');

  // Scroll to top when page loads and check for referral code
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track ViewContent event for Meta Pixel
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', {
        content_name: 'SubGuard Pricing Page',
        content_category: 'pricing',
        content_type: 'product_group'
      });
    }
    
    // Check for payment cancelled
    const payment = searchParams.get('payment');
    if (payment === 'cancelled') {
      toast.info('Payment cancelled. You can try again or choose a different plan.');
    }
    
    // Capture referral code from URL and store it
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      localStorage.setItem('referral_code', ref);
    } else {
      // Check localStorage for previously stored referral code
      const storedRef = localStorage.getItem('referral_code');
      if (storedRef) {
        setReferralCode(storedRef);
      }
    }
  }, [searchParams]);

  const handleSelectPlan = async (plan) => {
    // Free plan - go to signup or dashboard (check by planKey or name)
    if (plan.planKey === null || plan.planKey === undefined || plan.name === 'Free' || plan.name === 'Gratuit') {
      if (isAuthenticated) {
        navigate('/dashboard');
      } else {
        // Navigate to login page with signup mode, preserve referral code
        navigate(referralCode ? `/login?mode=signup&ref=${referralCode}` : '/login?mode=signup');
      }
      return;
    }

    // Paid plan - need to be logged in first
    if (!isAuthenticated) {
      // Store intended plan in localStorage, redirect to login
      localStorage.setItem('pending_plan', plan.planKey);
      navigate('/login');
      return;
    }

    // User already has this plan
    if (user?.subscription_plan === plan.planKey) {
      toast.info('You already have this plan!');
      return;
    }

    // Track InitiateCheckout event for Meta Pixel
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', {
        content_name: plan.name,
        content_category: 'subscription',
        content_ids: [plan.planKey],
        value: parseFloat(plan.price.replace(/[^0-9.]/g, '')) || 0,
        currency: 'USD'
      });
    }

    // Create Stripe checkout session
    setLoadingPlan(plan.planKey);
    try {
      const response = await api.post('/payments/create-checkout', {
        plan: plan.planKey,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(errorMsg);
      setLoadingPlan(null);
    }
  };

  const plans = [
    {
      name: t.pricing?.freeName || 'Free',
      price: t.pricing?.freePrice || '$0',
      period: t.pricing?.freePeriod || 'forever',
      description: 'Preview mode - see what you\'re missing',
      features: [
        'Preview up to 5 subscriptions',
        '7-day email scan preview',
        'Limited confidence scores',
        'Basic renewal alerts',
        'Bank statement preview (5 subs)'
      ],
      notIncluded: [
        'No export',
        'No savings total',
        'No full scan history'
      ],
      cta: t.pricing?.ctaGetStarted || 'Get Started',
      popular: false,
      planKey: null,
      isPreview: true
    },
    {
      name: t.pricing?.quickCheckName || 'Quick Check',
      price: t.pricing?.quickCheckPrice || '$4.99',
      period: t.pricing?.quickCheckPeriod || 'one-time',
      description: t.pricing?.quickCheckDesc || 'One-off 30-day email scan',
      features: [
        t.pricing?.singleScan || 'Single email account scan',
        t.pricing?.thirtyDayDetection || '30-day subscription detection',
        t.pricing?.aiAnalysis || 'AI-powered analysis',
        t.pricing?.exportResults || 'Export scan results',
        t.pricing?.noCommitment || 'No commitment required',
        'Unlimited bank statement import'
      ],
      notIncluded: [
        t.pricing?.ongoingMonitoring || 'Ongoing monitoring',
        t.pricing?.cancellationAssistant || 'Cancellation assistant',
        t.pricing?.renewalAlerts || 'Renewal alerts'
      ],
      cta: t.pricing?.ctaQuickCheck || 'Run Quick Check',
      popular: false,
      isQuickCheck: true,
      planKey: 'quick_check'
    },
    {
      name: t.pricing?.foundingName || 'Founding Guard',
      price: t.pricing?.foundingPrice || '$49',
      period: t.pricing?.foundingPeriod || 'one-time',
      description: t.pricing?.foundingDesc || 'Limited early access offer',
      features: [
        t.pricing?.fullAccess || 'Full SubGuard access',
        t.pricing?.emailScanning || 'Email scanning (Gmail)',
        t.pricing?.aiCancellation || 'AI-powered cancellation assistant',
        t.pricing?.negotiationGen || 'Negotiation email generator',
        t.pricing?.priorityAlerts || 'Priority renewal alerts',
        t.pricing?.savingsTracking || 'Savings tracking & analytics',
        t.pricing?.aiChat || 'AI Chat Assistant',
        t.pricing?.lifetimeCore || 'Lifetime core features access',
        'Unlimited bank statement import'
      ],
      notIncluded: [
        t.pricing?.futureBankInt || 'Future bank integrations',
        t.pricing?.advancedAuto || 'Advanced automation features',
        t.pricing?.businessFeatures || 'Business / team features'
      ],
      cta: t.pricing?.ctaFounding || 'Claim Early Access',
      popular: true,
      isFounder: true,
      planKey: 'founding'
    },
    {
      name: t.pricing?.monthlyName || 'Monthly',
      price: t.pricing?.monthlyPrice || '$2.99',
      period: t.pricing?.monthlyPeriod || '/month',
      description: t.pricing?.monthlyDesc || 'Best for active subscription managers',
      features: [
        t.pricing?.unlimitedSubs || 'Unlimited subscriptions',
        t.pricing?.emailScanning || 'Email scanning (Gmail)',
        t.pricing?.aiCancellation || 'AI-powered cancellation assistant',
        t.pricing?.negotiationGen || 'Negotiation email generator',
        t.pricing?.priorityAlerts || 'Priority renewal alerts',
        t.pricing?.savingsTracking || 'Savings tracking & analytics',
        t.pricing?.aiChat || 'AI Chat Assistant',
        t.pricing?.allFuture || 'All future features included',
        'Unlimited bank statement import'
      ],
      notIncluded: [],
      cta: t.pricing?.ctaMonthly || 'Start Monthly',
      popular: false,
      planKey: 'monthly'
    },
    {
      name: t.pricing?.yearlyName || 'Yearly',
      price: t.pricing?.yearlyPrice || '$29',
      period: t.pricing?.yearlyPeriod || '/year',
      description: t.pricing?.yearlyDesc || 'Save with annual billing',
      features: [
        t.pricing?.everythingMonthly || 'Everything in Monthly',
        t.pricing?.bestValue || 'Best value',
        t.pricing?.prioritySupport || 'Priority support',
        t.pricing?.earlyAccess || 'Early access to new features',
        t.pricing?.aiChat || 'AI Chat Assistant',
        t.pricing?.allFuture || 'All future features included',
        'Unlimited bank statement import'
      ],
      notIncluded: [],
      cta: t.pricing?.ctaYearly || 'Start Yearly',
      popular: false,
      savings: t.pricing?.badgeBestValue || 'Best Value',
      planKey: 'yearly'
    }
  ];

  // Check for pending plan selection from URL (after login redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectPlan = params.get('select');
    if (selectPlan && isAuthenticated) {
      // Find the plan and trigger checkout
      const planToSelect = plans.find(p => p.planKey === selectPlan);
      if (planToSelect) {
        handleSelectPlan(planToSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const featureComparison = [
    { feature: t.pricing?.compEmailScanning || 'Email scanning', founding: true, standard: true },
    { feature: t.pricing?.compRenewalAlerts || 'Renewal alerts', founding: true, standard: true },
    { feature: t.pricing?.compCancelWorkflows || 'Cancel-for-me workflows', founding: true, standard: true },
    { feature: t.pricing?.compSavingsDash || 'Savings dashboard', founding: true, standard: true },
    { feature: 'Bank statement import', founding: true, standard: true },
    { feature: t.pricing?.compBankInt || 'Bank integrations (future)', founding: false, standard: true },
    { feature: t.pricing?.compAdvancedAuto || 'Advanced automation', founding: false, standard: true },
    { feature: t.pricing?.compBusiness || 'Business / team features', founding: false, standard: true },
  ];

  const trustSignals = [
    { icon: Shield, text: t.pricing?.trustNoBank || 'No bank connection required' },
    { icon: Eye, text: t.pricing?.trustReadOnly || 'Read-only email access' },
    { icon: Unplug, text: t.pricing?.trustDisconnect || 'Disconnect anytime' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pricing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-xl"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                SubGuard
              </span>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/login')}
            data-testid="login-btn"
          >
            {t.pricing?.logIn || 'Log in'}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            {t.pricing?.heroTitle || 'Simple, Transparent Pricing'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            {t.pricing?.heroSubtitle || 'Choose the plan that works best for you. All plans include core features to help you save money.'}
          </p>
          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-6">
            {trustSignals.map((signal) => (
              <div key={signal.text} className="flex items-center gap-2 text-gray-600">
                <signal.icon className="w-4 h-4 text-[#064E3B]" />
                <span className="text-sm">{signal.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Guard Banner */}
      <section className="py-8 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f]" data-testid="founding-banner">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#6ee7b7]/20 rounded-2xl flex items-center justify-center">
                <Zap className="w-7 h-7 text-[#6ee7b7]" />
              </div>
              <div className="text-white">
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
                  {t.pricing?.foundingBannerTitle || 'Founding Guard — Limited Early Access'}
                </h3>
                <p className="text-blue-100 text-sm">
                  {t.pricing?.foundingBannerSubtitle || 'One-time $49 payment • Lifetime core features • Available for a short time'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{t.pricing?.limitedTime || 'Limited time offer'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.name}
                className={`relative border shadow-soft transition-all hover:shadow-lg ${
                  plan.isFounder 
                    ? 'border-2 border-[#1e3a5f] bg-gradient-to-b from-[#1e3a5f]/5 to-transparent' 
                    : plan.isQuickCheck
                    ? 'border-2 border-[#EA580C] bg-gradient-to-b from-[#EA580C]/5 to-transparent'
                    : plan.popular 
                    ? 'border-2 border-[#064E3B]' 
                    : 'border-gray-100'
                }`}
              >
                {plan.isFounder && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#1e3a5f] text-white border-0 px-4 py-1 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {t.pricing?.badgeEarlyAccess || 'Early Access'}
                    </Badge>
                  </div>
                )}
                {plan.isQuickCheck && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#EA580C] text-white border-0 px-4 py-1 flex items-center gap-1">
                      <Search className="w-3 h-3" />
                      {t.pricing?.badgeTryFirst || 'Try First'}
                    </Badge>
                  </div>
                )}
                {plan.savings && (
                  <div className="absolute -top-4 right-4">
                    <Badge className="bg-[#EA580C] text-white border-0 px-3 py-1">
                      {plan.savings}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pt-8 pb-4">
                  <CardTitle className="text-xl" style={{ fontFamily: 'Outfit' }}>
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  <CardDescription className="mt-2">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-8">
                  <ul className="space-y-3 mb-4">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          plan.isQuickCheck ? 'text-[#EA580C]' : 'text-[#064E3B]'
                        }`} />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.notIncluded.length > 0 && (
                    <div className="mb-6 pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-400 mb-2">{t.pricing?.notIncluded || 'Not included:'}</p>
                      <ul className="space-y-1">
                        {plan.notIncluded.map((feature) => (
                          <li key={feature} className="text-xs text-gray-400 line-through">
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={loadingPlan !== null && loadingPlan === plan.planKey}
                    className={`w-full rounded-xl ${
                      plan.isFounder
                        ? 'bg-[#1e3a5f] hover:bg-[#152a45] text-white'
                        : plan.isQuickCheck
                        ? 'bg-[#EA580C] hover:bg-[#DC4C08] text-white'
                        : plan.popular
                        ? 'bg-[#064E3B] hover:bg-[#053D2E] text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                    data-testid={`cta-${plan.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {loadingPlan !== null && loadingPlan === plan.planKey ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.pricing?.processing || 'Processing...'}</>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Check Explanation */}
      <section className="py-12 bg-[#FFF7ED]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EA580C]/10 text-[#EA580C] rounded-full text-sm font-medium mb-4">
            <Search className="w-4 h-4" />
            {t.pricing?.badgeTryFirst || 'Try Before You Commit'}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            {t.pricing?.quickCheckSectionTitle || 'Not Sure? Run a Quick Check First'}
          </h2>
          <p className="text-gray-600 mb-6">
            {t.pricing?.quickCheckSectionDesc || 'For just $4.99, scan one email account and see what subscriptions we find in the last 30 days. No commitment, no subscription — just clarity on what you\'re paying for.'}
          </p>
          <Button 
            onClick={() => handleSelectPlan({ planKey: 'quick_check', cta: t.pricing?.quickCheckCta || 'Run Quick Check — $4.99' })}
            disabled={loadingPlan === 'quick_check'}
            className="bg-[#EA580C] hover:bg-[#DC4C08] text-white rounded-xl px-8"
          >
            {loadingPlan === 'quick_check' ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.pricing?.processing || 'Processing...'}</>
            ) : (
              t.pricing?.quickCheckCta || 'Run Quick Check — $4.99'
            )}
          </Button>
        </div>
      </section>

      {/* Founding Guard Explanation */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8" style={{ fontFamily: 'Outfit' }}>
            {t.pricing?.comparisonTitle || 'Founding Guard vs. Standard Plans'}
          </h2>
          
          <div className="bg-[#f8fafc] rounded-2xl p-6 mb-8 border border-gray-100">
            <p className="text-gray-600 text-sm leading-relaxed">
              {t.pricing?.comparisonDesc || 'Founding Guard members get full access to SubGuard\'s core protection features with a one-time payment. This plan is limited and will not be offered again. As SubGuard evolves, new advanced features may be introduced under new plans.'}
            </p>
          </div>

          {/* Feature Comparison Table */}
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">{t.pricing?.featureCol || 'Feature'}</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-[#1e3a5f]">{t.pricing?.foundingCol || 'Founding Guard'}</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-[#064E3B]">{t.pricing?.standardCol || 'Standard Sub'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {featureComparison.map((row) => (
                  <tr key={row.feature} className="bg-white">
                    <td className="px-6 py-4 text-sm text-gray-600">{row.feature}</td>
                    <td className="text-center px-6 py-4">
                      {row.founding ? (
                        <Check className="w-5 h-5 text-[#1e3a5f] mx-auto" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="text-center px-6 py-4">
                      {row.standard ? (
                        <Check className="w-5 h-5 text-[#064E3B] mx-auto" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8" style={{ fontFamily: 'Outfit' }}>
            {t.pricing?.faqTitle || 'Frequently Asked Questions'}
          </h2>
          <div className="space-y-6">
            <div className="p-6 bg-white rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq1Title || 'What is Founding Guard?'}</h3>
              <p className="text-gray-600 text-sm">
                {t.pricing?.faq1Desc || 'Founding Guard is a limited early access offer for our earliest supporters. Pay once, get lifetime access to all core SubGuard features. This offer is only available for a short time and won\'t be repeated.'}
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq2Title || 'Will Founding Guard members ever lose access?'}</h3>
              <p className="text-gray-600 text-sm">
                {t.pricing?.faq2Desc || 'Founding Guard members keep access to all core features for as long as the plan is supported. However, future premium features like bank integrations and advanced automation will only be available through subscription plans.'}
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq3Title || 'Is there a mobile app?'}</h3>
              <p className="text-gray-600 text-sm">
                {t.pricing?.faq3Desc || 'SubGuard is web-first, fully optimised for both mobile and desktop browsers. A companion app for notifications and quick insights is coming soon.'}
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq4Title || 'Is my data secure?'}</h3>
              <p className="text-gray-600 text-sm">
                {t.pricing?.faq4Desc || 'Yes! We use read-only access to your email and never store your email content. We only extract subscription-related metadata.'}
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">{t.pricing?.faq5Title || 'Do you connect to my bank?'}</h3>
              <p className="text-gray-600 text-sm">
                {t.pricing?.faq5Desc || 'No. SubGuard never connects to your bank account. We only scan emails for subscription receipts and renewal notices.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                SubGuard
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} SubGuard. {t.pricing?.footerRights || 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
